import React from 'react';
import { isEqual } from 'lodash';
import { Box } from '@mui/material';

import { AutocompleteOption, FormInputProps } from '@occc-app/design-system';

type ValidationResult = { isValid: boolean; validationError: string };

type ValidatableFormEvent = React.ChangeEvent<HTMLInputElement>;
export type FormEventValidationCheck<VW> = (
  event: ValidatableFormEvent,
  validateWith?: VW,
) => ValidationResult;

type ValidatableAutocompleteValue = AutocompleteOption[] | AutocompleteOption | null;
export type AutocompleteValidationCheck<VW> = (
  value: ValidatableAutocompleteValue,
  validateWith?: VW,
) => ValidationResult;

type ValidationCheck<VW> = FormEventValidationCheck<VW> | AutocompleteValidationCheck<VW>;

export const numberValidation: FormEventValidationCheck<{}> = (event) => {
  const { value } = event.currentTarget;
  const isValid = !!(value && !isNaN(Number(value)));
  const validationError = isValid ? '' : 'Must be a number';
  return {
    isValid,
    validationError,
  };
};

export const greaterThanZeroValidation: FormEventValidationCheck<{}> = (event) => {
  const { value } = event.currentTarget;
  const { isValid: isNumber } = numberValidation(event, {});
  let isValid = false;
  if (isNumber) {
    isValid = Number(value) > 0;
  }
  const validationError = isValid ? '' : 'Must be greater than zero';
  return {
    isValid,
    validationError,
  };
};

export const autocompleteLengthValidation: AutocompleteValidationCheck<{}> = (value) => {
  const isValid = Array.isArray(value) ? value.length > 0 : !!value;
  const validationError = isValid ? '' : 'Must select at least one';
  return {
    isValid,
    validationError,
  };
};

export const requiredValidation: FormEventValidationCheck<{}> = (event) => {
  const { value } = event.currentTarget;
  const isValid = value !== null && value !== '';
  const validationError = isValid ? '' : 'Required';
  return {
    isValid,
    validationError,
  };
};

export const urlValidation: FormEventValidationCheck<{}> = (event) => {
  const { value } = event.currentTarget;
  const isValid =
    value === '' ||
    !!value.match(/^http(s)?:\/\/[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/);
  const validationError = isValid ? '' : 'Please provide a valid URL (e.g. https://www.stuzo.com )';
  return {
    isValid,
    validationError,
  };
};

type ExecuteFormEventValidationChecks = <VW>(
  validations: FormEventValidationCheck<VW>[],
  event: ValidatableFormEvent,
  validateWith?: VW,
) => { isValid: boolean; validationError: string };
export const executeFormEventValidationChecks: ExecuteFormEventValidationChecks = (
  validations,
  event,
  validateWith,
) => {
  return validations.reduce(
    (reducedChecks, validation) => {
      const { isValid, validationError } = validation(event, validateWith);
      const { isValid: currentValidity, validationError: currentError } = reducedChecks;
      return {
        isValid: currentValidity && isValid,
        validationError: `${currentError}${
          validationError ? `${currentError ? ', ' : ''}${validationError}` : ''
        }`,
      };
    },
    { isValid: true, validationError: '' },
  );
};

type FormEventWrapperComponentState = {
  value: any;
  isValid: boolean;
  validationError: string;
  prevChangeEvent: ValidatableFormEvent | null;
  prevChangeEventTarget: ValidatableFormEvent['currentTarget'] | null;
};

type WrapperComponentClass<K> = new (...args: any[]) => React.Component<K, any>;
type WithFormEventValidation = <IProps extends FormInputProps, IValidateWith extends {}>(
  WrappedComponent: React.FC<IProps>,
  ...validationChecks: ValidationCheck<IValidateWith>[]
) => WrapperComponentClass<IProps & { validateWith?: IValidateWith }>;

const withFormEventValidation: WithFormEventValidation = <
  IProps extends FormInputProps,
  IValidateWith extends {},
>(
  WrappedComponent: any,
  ...validationChecks: any[]
) => {
  class WrapperComponent extends React.Component<
    IProps & { validateWith?: IValidateWith },
    FormEventWrapperComponentState
  > {
    constructor(props: any) {
      super(props);
      this.state = {
        value: props.value,
        isValid: true,
        validationError: '',
        prevChangeEvent: null,
        prevChangeEventTarget: null,
      };
    }

    componentDidUpdate(prevProps: IProps & { validateWith?: IValidateWith }) {
      const { validateWith: validateWithPrev } = prevProps;
      const { validateWith } = this.props;

      if (isEqual(validateWith, validateWithPrev)) {
        return;
      }

      const { prevChangeEvent, prevChangeEventTarget } = this.state;
      if (prevChangeEvent === null || prevChangeEventTarget === null) {
        return;
      }

      // Re-simulate previous change event with the previously processed `currentTarget` injected
      const event = prevChangeEvent;
      event.currentTarget = prevChangeEventTarget;
      this.validationOnChange(event);
    }

    validationOnChange = (event: ValidatableFormEvent) => {
      const { isValid, validationError } = executeFormEventValidationChecks<IValidateWith>(
        validationChecks,
        event,
        this.props.validateWith,
      );

      if (event.persist) {
        event.persist();
      }

      this.setState({
        isValid,
        validationError,
        value: event.currentTarget ? event.currentTarget.value : event,
        prevChangeEvent: event,
        prevChangeEventTarget: event.currentTarget,
      });

      if (isValid && this.props.onChange) {
        this.props.onChange(event);
      }
    };

    render() {
      const { isValid, value: valueInState } = this.state;
      const value = isValid ? this.props.value : valueInState;

      const { validateWith, ...propsWithoutValidateWith } = this.props;
      // Could not use the spread operator here due to a bug in TypeScript, tracked here:
      // https://github.com/Microsoft/TypeScript/issues/10727
      // tslint:disable-next-line
      const newProps = Object.assign({}, propsWithoutValidateWith, {
        onChange: this.validationOnChange,
        value,
      });

      return (
        <WrappedComponent errorText={this.state.validationError} isValid={isValid} {...newProps} />
      );
    }
  }
  return WrapperComponent;
};

type ExecuteAutocompleteValueValidationChecks = <VW>(
  validations: AutocompleteValidationCheck<VW>[],
  value: ValidatableAutocompleteValue,
  validateWith?: VW,
) => { isValid: boolean; validationError: string };
export const executeAutocompleteValueValidationChecks: ExecuteAutocompleteValueValidationChecks = (
  validations,
  value,
  validateWith,
) => {
  return validations.reduce(
    (reducedChecks, validation) => {
      const { isValid, validationError } = validation(value, validateWith);
      const { isValid: currentValidity, validationError: currentError } = reducedChecks;
      return {
        isValid: currentValidity && isValid,
        validationError: `${currentError}${
          validationError ? `${currentError ? ', ' : ''}${validationError}` : ''
        }`,
      };
    },
    { isValid: true, validationError: '' },
  );
};

type AutocompleteWrapperComponentProps = {
  onChange: (event: React.ChangeEvent<{}>, value: ValidatableAutocompleteValue) => void;
  value: any;
};

type AutocompleteWrapperComponentState = {
  value: any;
  isValid: boolean;
  validationError: string;
  prevEvent: React.ChangeEvent<{}> | null;
  prevValue: ValidatableAutocompleteValue | null;
};

type WithAutocompleteValueValidation = <
  IProps extends AutocompleteWrapperComponentProps,
  IValidateWith extends {},
>(
  WrappedComponent: React.FC<IProps>,
  ...validationChecks: ValidationCheck<IValidateWith>[]
) => WrapperComponentClass<IProps & { validateWith?: IValidateWith }>;

const withAutocompleteValueValidation: WithAutocompleteValueValidation = <
  IProps extends AutocompleteWrapperComponentProps,
  IValidateWith extends {},
>(
  WrappedComponent: any,
  ...validationChecks: any[]
) => {
  class WrapperComponent extends React.Component<
    IProps & { validateWith?: IValidateWith },
    AutocompleteWrapperComponentState
  > {
    constructor(props: any) {
      super(props);
      this.state = {
        value: props.value,
        isValid: true,
        validationError: '',
        prevEvent: null,
        prevValue: null,
      };
    }

    componentDidUpdate(prevProps: IProps & { validateWith?: IValidateWith }) {
      const { validateWith: validateWithPrev } = prevProps;
      const { validateWith } = this.props;

      if (isEqual(validateWith, validateWithPrev)) {
        return;
      }

      const { prevEvent, prevValue } = this.state;
      if (prevEvent === null) {
        return;
      }

      // Re-simulate previous change event with the previously processed `currentTarget` injected
      this.validationOnChange(prevEvent, prevValue);
    }

    validationOnChange = (event: React.ChangeEvent<{}>, value: ValidatableAutocompleteValue) => {
      const { isValid, validationError } = executeAutocompleteValueValidationChecks<IValidateWith>(
        validationChecks,
        value,
        this.props.validateWith,
      );

      this.setState({
        isValid,
        validationError,
        value,
        prevValue: value,
        prevEvent: event,
      });

      if (isValid) {
        this.props.onChange(event, value);
      }
    };

    render() {
      const { isValid, value: valueInState } = this.state;
      const value = isValid ? this.props.value : valueInState;

      // Could not use the spread operator here due to a bug in TypeScript, tracked here:
      // https://github.com/Microsoft/TypeScript/issues/10727
      // tslint:disable-next-line
      const newProps = Object.assign({}, this.props, {
        onChange: this.validationOnChange,
        value,
      });
      const inputProps = {
        errorText: this.state.validationError,
        isValid,
      };
      return (
        <Box>
          <WrappedComponent inputProps={inputProps} {...newProps} />
        </Box>
      );
    }
  }
  return WrapperComponent;
};

export { withFormEventValidation, withAutocompleteValueValidation };
